import { HubFeature } from '@ats/api/cm';
import { LocDefs, LocMap, MapLocKeys } from '@cm/core/localization';

const dpRoot = 'Catalogs|Achievements|Details';

const labelDescriptionStart = 'Label for:';

export type SPAchievementDetailsLocs =
	| 'SPAchievementDetailsSuccessNotification'
	| 'SPAchievementDetailsName'
	| 'SPAchievementDetailsNameRequired'
	| 'SPAchievementDetailsGroup'
	| 'SPAchievementDetailsDisplayOrder'
	| 'SPAchievementDetailsDisplayOrderError'
	| 'SPAchievementDetailsUrl'
	| 'SPAchievementDetailsUrlError'
	| 'SPAchievementDetailsStartDate'
	| 'SPAchievementDetailsStartDateError'
	| 'SPAchievementDetailsEndDate'
	| 'SPAchievementDetailsEndDateError'
	| 'SPAchievementDetailsTranscript'
	| 'SPAchievementDetailsDescription'
	| 'SPAchievementDetailsCancel'
	| 'SPAchievementDetailsUpdate'
	| 'SPAchievementDetailsTitle'
	| 'SPAchievementDetailsActiveStatus'
	| 'SPAchievementDetailsInactiveStatus'
	| 'SPAchievementDetailsEdit'
	| 'SPAchievementDetailsType'
	| 'SPAchievementDetailsDuration'
	| 'SPAchievementDetailsStatus'
	| 'SPAchievementDetailsId'
	| 'SPAchievementDetailsExternalId'
	| 'SPAchievementDetailsCadc'
	| 'SPAchievementDetailsTabTitle';

export const spAchievementDetailsLocs: LocDefs<SPAchievementDetailsLocs> = {
	SPAchievementDetailsTabTitle: {
		defaultValue: 'Achievement Details',
		description: 'Tab title and heading for the achievement details page',
		legacyName: 'Credential.Achievements/CatalogsAchievements.Details.Title.TabTitle',
		displayPath: `${dpRoot}|Tab Title`,
		displayType: 'Title',
		feature: HubFeature.CredentialAchievements,
		portal: 'SP',
	},
	SPAchievementDetailsSuccessNotification: {
		defaultValue: 'Changes saved.',
		description: 'Notification shown when achievement details are successfully changed',
		legacyName:
			'Credential.Achievements/CatalogsAchievements.Details.NotificationMessage.ChangesSaved',
		displayPath: `${dpRoot}|Name`,
		displayType: 'Label',
		feature: HubFeature.CredentialAchievements,
		portal: 'SP',
	},
	SPAchievementDetailsName: {
		defaultValue: 'Name',
		description: `${labelDescriptionStart} Name.`,
		legacyName: 'Credential.Achievements/CatalogsAchievements.Details.FieldLabel.Name',
		displayPath: `${dpRoot}|Name`,
		displayType: 'Label',
		feature: HubFeature.CredentialAchievements,
		portal: 'SP',
	},
	SPAchievementDetailsNameRequired: {
		defaultValue: 'Name is required',
		description: `Error showm when name is not provided.`,
		legacyName: 'Credential.Achievements/CatalogsAchievements.Details.FieldError.NameRequired',
		displayPath: `${dpRoot}|Name Required Error`,
		displayType: 'Error',
		feature: HubFeature.CredentialAchievements,
		portal: 'SP',
	},
	SPAchievementDetailsGroup: {
		defaultValue: 'Group',
		description: `${labelDescriptionStart} Group.`,
		legacyName: 'Credential.Achievements/CatalogsAchievements.Details.FieldLabel.Group',
		displayPath: `${dpRoot}|Group`,
		displayType: 'Label',
		feature: HubFeature.CredentialAchievements,
		portal: 'SP',
	},
	SPAchievementDetailsDisplayOrder: {
		defaultValue: 'Display Order',
		description: `${labelDescriptionStart} Display order.`,
		legacyName: 'Credential.Achievements/CatalogsAchievements.Details.FieldLabel.DisplayOrder',
		displayPath: `${dpRoot}|Display Order`,
		displayType: 'Label',
		feature: HubFeature.CredentialAchievements,
		portal: 'SP',
	},
	SPAchievementDetailsDisplayOrderError: {
		defaultValue: 'Display order must only contain numbers',
		description: `Error shown when display order is not valid.`,
		legacyName:
			'Credential.Achievements/CatalogsAchievements.Details.FieldError.DisplayOrderNumbers',
		displayPath: `${dpRoot}|Display Order Error`,
		displayType: 'Error',
		feature: HubFeature.CredentialAchievements,
		portal: 'SP',
	},
	SPAchievementDetailsUrl: {
		defaultValue: 'URL',
		description: `${labelDescriptionStart} URL.`,
		legacyName: 'Credential.Achievements/CatalogsAchievements.Details.FieldLabel.Url',
		displayPath: `${dpRoot}|URL`,
		displayType: 'Label',
		feature: HubFeature.CredentialAchievements,
		portal: 'SP',
	},
	SPAchievementDetailsUrlError: {
		defaultValue: 'Please enter a valid URL, such as https://example.com',
		description: `Error shown when URL is not valid.`,
		legacyName: 'Credential.Achievements/CatalogsAchievements.Details.FieldError.UrlInvalid',
		displayPath: `${dpRoot}|Url Invalid`,
		displayType: 'Error',
		feature: HubFeature.CredentialAchievements,
		portal: 'SP',
	},
	SPAchievementDetailsStartDate: {
		defaultValue: 'Start Date',
		description: `${labelDescriptionStart} Start date.`,
		legacyName: 'Credential.Achievements/CatalogsAchievements.Details.FieldLabel.StartDate',
		displayPath: `${dpRoot}|Start Date`,
		displayType: 'Label',
		feature: HubFeature.CredentialAchievements,
		portal: 'SP',
	},
	SPAchievementDetailsStartDateError: {
		defaultValue: 'The Start Date must come before the End Date',
		description: `Error shown when start date is not valid.`,
		legacyName:
			'Credential.Achievements/CatalogsAchievements.Details.FieldError.StartDateComparison',
		displayPath: `${dpRoot}|Start Date Invalid`,
		displayType: 'Error',
		feature: HubFeature.CredentialAchievements,
		portal: 'SP',
	},
	SPAchievementDetailsEndDate: {
		defaultValue: 'End Date',
		description: `${labelDescriptionStart} End date.`,
		legacyName: 'Credential.Achievements/CatalogsAchievements.Details.FieldLabel.EndDate',
		displayPath: `${dpRoot}|End Date`,
		displayType: 'Label',
		feature: HubFeature.CredentialAchievements,
		portal: 'SP',
	},
	SPAchievementDetailsEndDateError: {
		defaultValue: 'The End Date must come after the Start Date',
		description: `Error shown when end date is not valid.`,
		legacyName:
			'Credential.Achievements/CatalogsAchievements.Details.FieldError.EndDateComparison',
		displayPath: `${dpRoot}|End Date Invalid`,
		displayType: 'Error',
		feature: HubFeature.CredentialAchievements,
		portal: 'SP',
	},
	SPAchievementDetailsTranscript: {
		defaultValue: 'Transcript Enabled',
		description: `${labelDescriptionStart} Transcript.`,
		legacyName:
			'Credential.Achievements/CatalogsAchievements.Details.FieldLabel.TranscriptEnabled',
		displayPath: `${dpRoot}|Transcript Enabled`,
		displayType: 'Label',
		feature: HubFeature.CredentialAchievements,
		portal: 'SP',
	},
	SPAchievementDetailsDescription: {
		defaultValue: 'Description',
		description: `${labelDescriptionStart} Description.`,
		legacyName: 'Credential.Achievements/CatalogsAchievements.Details.FieldLabel.Description',
		displayPath: `${dpRoot}|Description`,
		displayType: 'Label',
		feature: HubFeature.CredentialAchievements,
		portal: 'SP',
	},
	SPAchievementDetailsCancel: {
		defaultValue: 'Cancel',
		description: 'Button to cancel editing',
		legacyName: 'Credential.Achievements/CatalogsAchievements.Details.Button.Cancel',
		displayPath: `${dpRoot}|Cancel`,
		displayType: 'Button',
		feature: HubFeature.CredentialAchievements,
		portal: 'SP',
	},
	SPAchievementDetailsUpdate: {
		defaultValue: 'Update',
		description: 'Button to update other achievement details',
		legacyName: 'Credential.Achievements/CatalogsAchievements.Details.Button.Update',
		displayPath: `${dpRoot}|Update`,
		displayType: 'Button',
		feature: HubFeature.CredentialAchievements,
		portal: 'SP',
	},
	SPAchievementDetailsTitle: {
		defaultValue: 'Details',
		description: 'Title for the achievement details page',
		legacyName: 'Credential.Achievements/CatalogsAchievements.Details.Title.Details',
		displayPath: `${dpRoot}|Cancel`,
		displayType: 'Button',
		feature: HubFeature.CredentialAchievements,
		portal: 'SP',
	},
	SPAchievementDetailsActiveStatus: {
		defaultValue: 'Active',
		description: 'Content displayed when achievement is active',
		legacyName: 'Credential.Achievements/CatalogsAchievements.Details.Content.StatusActive',
		displayPath: `${dpRoot}|Active Status`,
		displayType: 'Content',
		feature: HubFeature.CredentialAchievements,
		portal: 'SP',
	},
	SPAchievementDetailsInactiveStatus: {
		defaultValue: 'Inactive',
		description: 'Content displayed when achievement is inactive',
		legacyName: 'Credential.Achievements/CatalogsAchievements.Details.Content.StatusInactive',
		displayPath: `${dpRoot}|Active Status`,
		displayType: 'Content',
		feature: HubFeature.CredentialAchievements,
		portal: 'SP',
	},
	SPAchievementDetailsEdit: {
		defaultValue: 'Edit Details',
		description: 'Button to edit other achievement details',
		legacyName: 'Credential.Achievements/CatalogsAchievements.Details.Button.EditDetails',
		displayPath: `${dpRoot}|Cancel`,
		displayType: 'Button',
		feature: HubFeature.CredentialAchievements,
		portal: 'SP',
	},
	SPAchievementDetailsType: {
		defaultValue: 'Type',
		description: `${labelDescriptionStart} Type.`,
		legacyName: 'Credential.Achievements/CatalogsAchievements.Details.FieldLabel.Type',
		displayPath: `${dpRoot}|Type`,
		displayType: 'Label',
		feature: HubFeature.CredentialAchievements,
		portal: 'SP',
	},
	SPAchievementDetailsDuration: {
		defaultValue: 'Duration',
		description: `${labelDescriptionStart} Duration.`,
		legacyName: 'Credential.Achievements/CatalogsAchievements.Details.FieldLabel.Duration',
		displayPath: `${dpRoot}|Duration`,
		displayType: 'Label',
		feature: HubFeature.CredentialAchievements,
		portal: 'SP',
	},
	SPAchievementDetailsStatus: {
		defaultValue: 'Status',
		description: `${labelDescriptionStart} Status.`,
		legacyName: 'Credential.Achievements/CatalogsAchievements.Details.FieldLabel.Status',
		displayPath: `${dpRoot}|Status`,
		displayType: 'Label',
		feature: HubFeature.CredentialAchievements,
		portal: 'SP',
	},
	SPAchievementDetailsId: {
		defaultValue: 'ID',
		description: `${labelDescriptionStart} Id.`,
		legacyName: 'Credential.Achievements/CatalogsAchievements.Details.FieldLabel.Id',
		displayPath: `${dpRoot}|Id`,
		displayType: 'Label',
		feature: HubFeature.CredentialAchievements,
		portal: 'SP',
	},
	SPAchievementDetailsExternalId: {
		defaultValue: 'External ID',
		description: `${labelDescriptionStart} ExternalId.`,
		legacyName: 'Credential.Achievements/CatalogsAchievements.Details.FieldLabel.ExternalId',
		displayPath: `${dpRoot}|ExternalId`,
		displayType: 'Label',
		feature: HubFeature.CredentialAchievements,
		portal: 'SP',
	},
	SPAchievementDetailsCadc: {
		defaultValue: 'CADC Exclude',
		description: `${labelDescriptionStart} CADC Exclude.`,
		legacyName: 'Credential.Achievements/CatalogsAchievements.Details.FieldLabel.CadcExclude',
		displayPath: `${dpRoot}|CADC Exclude`,
		displayType: 'Label',
		feature: HubFeature.CredentialAchievements,
		portal: 'SP',
	},
};

export type SPAchievementDetailsLocKeys = LocMap<SPAchievementDetailsLocs>;

export const spAchievementDetailsLocKeys: SPAchievementDetailsLocKeys =
	MapLocKeys<SPAchievementDetailsLocs>(spAchievementDetailsLocs);
